import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn, useSize } from 'ahooks';
import {
  Empty,
  Input,
  Modal,
  Skeleton,
  Space,
  Tag,
  Tree,
} from '@bedrock/components';
import { ProFilter } from '@bedrock/eed-react';
import NoResults from '@bedrock/components/es/Empty/images/middle/NoResults';
import classNames from 'classnames/bind';
import cn from 'classnames';
import withFunctionCall from '#utils/withFunctionCall';
import { treeOptionsFilter } from '#utils/tree';
import { useDemandOptions, useDemandOptionsWithCache } from '#features';
import { filterSupplierList as filterSupplierListByStrategy } from '#features/MPCDemand/utils';
import { enums } from '#services/api';
import styles from './AddSupplierModal.less';

const cx = classNames.bind(styles);

// ============ 类型定义 ============

/** 供应商树节点渲染组件类型 */
export type SupplierTitleRender = (nodeData: any) => React.ReactNode;

/**
 * AddSupplierModal.show() 入参（合并配置 + 运行时参数）
 */
export interface AddSupplierModalShowProps {
  // ---- 静态配置 ----
  /** 弹窗标题 */
  title?: string;
  /** 弹窗宽度 */
  width?: number;
  /** 供应商树节点渲染组件（必填，由消费方注入，如 SupplierItem/TreeItem） */
  titleRender: SupplierTitleRender;
  /** 自定义筛选字段配置（不传则使用默认：服务类型 + 供应商标签 + 历史承接费用类型） */
  changeFieldOptions?: any[];

  // ---- 运行时入参 ----
  /** 已选中的供应商列表 */
  selectedSupplyList?: any[];
  /** 禁用的供应商列表 */
  disabledList?: { supplier_id: string }[];
  /** 隐藏的供应商列表 */
  hideList?: { supplier_id: string }[];
  /** 外部传入的供应商选项（不传则自动请求） */
  supplierOptions?: any[];
  /** 需求单详情 */
  demandDetail?: any;
  /** 信息展示模式 */
  infoType?: 'simple' | 'detail';
  /** 采购策略 */
  purchasing_strategy?: string;
  /** 采购方式 */
  purchasing_method?: string;
  /** 是否是 CDG 业务 */
  isCDGBussiness?: boolean;
  /** 仅搜索模式（不显示列表，需输入关键词才搜索） */
  searchMode?: boolean;
  /** 选择模式 */
  selectMode?: 'single' | 'multiple';
  /** 是否显示风险标签 */
  showRiskTags?: boolean;
  /** 外部过滤函数 */
  filterSupplierList?: (val: {
    supplierOptionsV1: any[];
    supplierOptions: any[];
  }) => any[];
  /** 请求供应商列表参数 */
  requestParams?: Record<string, any>;
  /** 是否请求旧版供应商列表 */
  withOldSupplierOptions?: boolean;
  /** 确认回调（用户点击 OK 时触发，回调参数为已选供应商列表） */
  onConfirm?: (selectedList: any[]) => void;
}

// ============ 内部工具 ============

const formatTagTree = (arr: any[] = []) => {
  return arr.map((item) => {
    const { children = [], tags = [] } = item;
    let newChildren: any[] = [];
    if (children?.length) {
      newChildren = formatTagTree(children);
    }
    const c = [...tags, ...newChildren];
    return { ...item, children: c };
  });
};

/** 默认服务类型选项（从 enums 构造，过滤掉 '90'） */
function getDefaultSeverTypeOptions() {
  try {
    return (
      enums?.ChannelTypeEnum?.values?.()?.filter(
        (item: any) => item.value !== '90',
      ) || []
    );
  } catch {
    return [];
  }
}

// ============ 内部组件 ============

const AddSupplierModalInner = ({
  // 静态配置
  title = '选择供应商',
  width = 880,
  titleRender,
  changeFieldOptions: externalChangeFieldOptions,
  // 运行时
  selectedSupplyList: incomingSelectedSupplyList,
  disabledList,
  hideList,
  supplierOptions: incomingSupplierOptions,
  demandDetail,
  infoType,
  purchasing_strategy,
  purchasing_method,
  isCDGBussiness,
  searchMode,
  selectMode,
  filterSupplierList: filterSupplierListProp,
  requestParams,
  withOldSupplierOptions,
  onConfirm,
  // withFunctionCall 注入：visible / onCancel(=destroy) / onClose(=destroy)
  ...rest
}: AddSupplierModalShowProps & Record<string, any>) => {
  // ---- 本地状态 ----
  const [selectSupplyList, setSelectSupplyList] = useState<any[]>(
    () => incomingSelectedSupplyList || [],
  );
  const [supplierOptions, setSupplierOptions] = useState<any[]>(
    () => incomingSupplierOptions || [],
  );

  const treeRef = useRef<any>();
  const filterRef = useRef<any>();
  const treeParentRef = useRef<any>();
  const searchRef = useRef<any>();
  useSize(treeParentRef);

  const [searchName, setSearchName] = useState('');
  const [searchParams, setSearchParams] = useState<any[]>([]);
  const isSingleMode = selectMode === 'single';
  const height60 =
    typeof window !== 'undefined' ? window.innerHeight * 0.6 : 560;

  // ---- 数据获取 ----
  const {
    loading: loadingDemandSuppliersNew,
    cacheHit: cacheHitDemandSuppliersNew,
    optionsListMap: optionsListMapDemandSuppliersNew,
    run: runDemandSuppliersNew,
  } = useDemandOptionsWithCache({
    keys: [
      'demand_suppliers_new',
      withOldSupplierOptions && 'demand_suppliers',
    ].filter(Boolean) as string[],
    demandDetail,
    params: {
      ...(requestParams || {}),
      ...(purchasing_method ? { purchasing_method } : {}),
    },
    cache: true,
    manual: true,
  });

  const { optionsMap, getOptionsMap } = useDemandOptions({
    keys: ['supplier_tag', 'demand_categories', 'supplier_tag_tree'],
    demandDetail,
    cache: true,
    manual: true,
  });

  // ---- 挂载时按需触发请求 ----
  useEffect(() => {
    getOptionsMap();
    if (!incomingSupplierOptions?.length) {
      runDemandSuppliersNew();
    }
    // 仅挂载时执行一次（命令式弹窗每次 show 都会创建新实例）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- 异步拉取结果回填到 supplierOptions ----
  useEffect(() => {
    const supplierOptionsV1 =
      optionsListMapDemandSuppliersNew?.demand_suppliers_new ?? [];
    const supplierOptionsOld =
      optionsListMapDemandSuppliersNew?.demand_suppliers ?? [];
    if (supplierOptionsV1?.length || supplierOptionsOld?.length) {
      const result =
        filterSupplierListProp?.({
          supplierOptionsV1,
          supplierOptions: supplierOptionsOld,
        }) ?? supplierOptionsV1;
      setSupplierOptions(result);
    }
  }, [
    loadingDemandSuppliersNew,
    cacheHitDemandSuppliersNew,
    optionsListMapDemandSuppliersNew,
    filterSupplierListProp,
  ]);

  // ---- 选中处理 ----
  const onCheckSupplier = useMemoizedFn((_checkedKeys: any, info: any) => {
    const { checked, node } = info;
    if (isSingleMode) {
      setSelectSupplyList(checked ? [node] : []);
    } else {
      if (checked) {
        setSelectSupplyList([...selectSupplyList, node]);
      } else {
        setSelectSupplyList(
          selectSupplyList.filter((item) => item?.value !== node?.value),
        );
      }
    }
  });

  // ---- 入参过滤 + 禁用/隐藏供应商 ----
  const newOptions = useMemo(() => {
    const ids = disabledList?.map((item) => item?.supplier_id);
    const supplierOptionsTemp = filterSupplierListByStrategy(supplierOptions, {
      purchasing_method: purchasing_method || demandDetail?.purchasing_method,
      purchasing_strategy:
        purchasing_strategy || demandDetail?.purchasing_strategy,
    });
    return supplierOptionsTemp
      ?.map?.((item: any) => ({
        ...item,
        children: item.children
          ?.filter(
            (el: any) =>
              !hideList?.find?.((ele: any) => ele.supplier_id === el.value),
          )
          ?.map((it: any) => {
            if (ids?.includes(it?.value)) {
              return { ...it, disabled: true };
            }
            return it;
          }),
      }))
      .filter((n: any) => !!n?.children?.length);
  }, [
    supplierOptions,
    disabledList,
    hideList,
    demandDetail?.purchasing_method,
    demandDetail?.purchasing_strategy,
    purchasing_method,
    purchasing_strategy,
  ]);

  // ---- 搜索过滤 ----
  const searchResultOptions = useMemo(() => {
    const channel_types =
      searchParams?.find((item) => item?.value === 'supplier_channel_types')
        ?.filterValue || [];
    const supplier_tag_Ids =
      searchParams?.find((item) => item?.value === 'supplier_tag_Ids')
        ?.filterValue || [];
    const demand_categories_ids =
      searchParams?.find((item) => item?.value === 'demand_categories_ids')
        ?.filterValue || [];

    return treeOptionsFilter(newOptions, (item: any) => {
      if (
        searchName &&
        !item.name
          ?.toLowerCase?.()
          ?.includes(searchName.toLocaleLowerCase())
      ) {
        return false;
      }
      if (
        channel_types?.length &&
        !channel_types.includes(item?.channel_type)
      ) {
        return false;
      }
      if (
        supplier_tag_Ids?.length &&
        !supplier_tag_Ids.some((tag: any) =>
          item?.tags?.some((n: any) => n?.id === tag),
        )
      ) {
        return false;
      }
      const l3_category_ids = item.l3_category_ids || [];
      if (
        demand_categories_ids?.length &&
        !demand_categories_ids.find((el: any) =>
          l3_category_ids.find((cid: any) =>
            String(cid)?.startsWith(String(el)),
          ),
        )
      ) {
        return false;
      }
      return true;
    });
  }, [newOptions, searchName, searchParams]);

  // ---- 筛选字段 ----
  const changeFieldOptions = useMemo(() => {
    if (externalChangeFieldOptions) return externalChangeFieldOptions;

    return [
      {
        name: '服务类型',
        type: 'select_multiple',
        multiple: true,
        value: 'supplier_channel_types',
        filterComponentProps: {
          options: getDefaultSeverTypeOptions(),
        },
      },
      infoType !== 'simple' && {
        name: '供应商标签',
        value: 'supplier_tag_Ids',
        type: 'select_multiple',
        filterComponentProps: {
          options: formatTagTree(optionsMap?.supplier_tag_tree?.options),
          multiple: true,
          returnOnlyLeaf: true,
        },
      },
      isCDGBussiness && {
        name: '历史承接费用类型',
        value: 'demand_categories_ids',
        type: 'select_multiple',
        filterComponentProps: {
          options: optionsMap['demand_categories']?.options || [],
          multiple: true,
        },
      },
    ].filter(Boolean);
  }, [externalChangeFieldOptions, optionsMap, infoType, isCDGBussiness]);

  // ---- 操作函数 ----
  const deleteSupplier = useMemoizedFn((data: any) => {
    setSelectSupplyList(
      selectSupplyList.filter((item) => item.value !== data.value),
    );
  });

  const handleOk = useMemoizedFn(() => {
    onConfirm?.(selectSupplyList);
    rest?.onCancel?.();
  });

  // ---- 渲染 ----
  return (
    <Modal
      {...rest}
      title={title}
      width={width}
      onOk={handleOk}
      onCancel={() => rest?.onCancel?.()}
      maskClosable={false}
      destroyOnClose>
      <div className="flex flex-col overflow-hidden">
        {/* 搜索区域 */}
        <Space size={[12, 0]} align="start" className="flex-none z-10">
          <ProFilter
            ref={filterRef}
            disableAddButton
            fieldOptions={changeFieldOptions}
            addonBefore={
              <Input.Search
                style={{ width: 280 }}
                ref={searchRef}
                onChange={(e: any) => setSearchName(e.target.value)}
                onBlur={(e: any) => setSearchName(e.target.value)}
                placeholder="请输入供应商名称"
              />
            }
            onReset={() => {
              searchRef.current?.setState?.({ value: '' });
              setSearchParams([]);
              setSearchName('');
            }}
            showCondition={false}
            onSearch={setSearchParams}>
            <ProFilter.FilterGroup />
          </ProFilter>
        </Space>

        {/* 已选标签区域 */}
        <div className="flex-none mb-1 max-h-[100px] overflow-y-auto mt-3">
          <span
            className={`inline-flex text-sm leading-[22px] mr-3 h-6 items-center ${
              !selectSupplyList?.length ? 'mb-1' : ''
            }`}>
            已选
            <span className="text-color-1">{selectSupplyList?.length}</span>项
          </span>
          {selectSupplyList?.map((item: any, index: number) => (
            <Tag
              color="light-brand"
              key={index}
              size="xSmall"
              closable
              onClose={() => deleteSupplier(item)}
              text={`${item.name}(${item.channel_type_name})`}
              className="inline-flex mr-1 h-6 mb-1"
            />
          ))}
        </div>

        {/* 供应商树区域 */}
        <div className={cn(cx('add-supplier-modal-tree'), 'flex-1 min-h-[400px]')} ref={treeParentRef}>
          {searchMode && !searchName ? (
            <Empty
              className="mt-5"
              imageNode={<NoResults />}
              description="请输入供应商名称搜索"
            />
          ) : (
            <>
              {loadingDemandSuppliersNew &&
                (searchResultOptions?.length ?? 0) === 0 && (
                  <div>{new Array(5).fill(<Skeleton active />)}</div>
                )}
              {!loadingDemandSuppliersNew &&
                searchResultOptions?.length === 0 && (
                  <Empty
                    className="mt-5"
                    imageNode={<NoResults />}
                    description="无搜索结果"
                  />
                )}
                {searchResultOptions?.length > 0 && (
                  <Tree
                    virtual
                    treeData={searchResultOptions}
                    key={JSON.stringify(searchResultOptions?.map((item: any) => item.value))}
                    showIcon={false}
                    defaultExpandAll
                    ref={treeRef}
                    height={height60}
                    onExpand={() => {}}
                    onCheck={onCheckSupplier}
                  checkable
                  checkStrictly={isSingleMode}
                  checkedKeys={selectSupplyList?.map?.(
                    (item: any) => item?.key,
                  )}
                  isCheckable={(item: any) => !item?.children?.length}
                  titleRender={titleRender}
                  className={isSingleMode ? 'supplier-single-mode' : ''}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

AddSupplierModalInner.displayName = 'AddSupplierModal';

// ============ 导出：withFunctionCall 包装，暴露 .show() 命令式调用 ============

const AddSupplierModalRaw = withFunctionCall(AddSupplierModalInner);

/**
 * 命令式打开"供应商选择"弹窗，适用于"即用即销"场景。
 *
 * @example
 * ```ts
 * AddSupplierModal.show({
 *   titleRender: SupplierItem,
 *   selectedSupplyList: currentList,
 *   demandDetail,
 *   selectMode: 'multiple',
 *   onConfirm: (list) => setSupplyList(list),
 * });
 * ```
 */
export const AddSupplierModal = {
  show(config: AddSupplierModalShowProps) {
    return AddSupplierModalRaw.show(config as any);
  },
};

export default AddSupplierModal;
